// Signal detail — a Library artifact alongside kernels/canvases/workflows.
//
// A SIGNAL is a built-in security signal computed by the backend signal pipeline
// (signal_tags). Signals ship natively and are read-only; selected from the
// Library "Signals" section, this page is its read-only detail surface: the
// prompts that fired it (server-scoped via getTraces — no in-browser scan) plus
// the read-only definition in the right rail.
import { useEffect, useMemo, useState } from "react";
import SplitPage from "../components/SplitPage.jsx";
import TabStrip from "../components/TabStrip.jsx";
import PageHero from "../components/PageHero.jsx";
import ScrollArea from "../components/ScrollArea.jsx";
import TraceTable from "../components/TraceTable.jsx";
import SignalSourceEditor from "../components/SignalSourceEditor.jsx";
import { NOTE_CLASS } from "../lib/ui.js";
import { useQuery } from "../lib/dataClient.js";
import { findTriggerByCommand, isDefaultTrigger, subscribeTriggers } from "../lib/triggers/triggerStore.js";
import { hasSensitivePermission, useMyPermissions } from "../lib/permissions.js";
import { signalSourceDefinition, useSignalSource } from "../lib/useSignalSource.js";
import { PERMISSIONS } from "../lib/permissionKeys.js";

const INPUT = "min-h-[28px] rounded-[2px] border border-[color:var(--line)] bg-transparent px-[9px] text-[12px] leading-[1.18] text-[color:var(--ink)] outline-none focus:border-[color:var(--line-strong)]";
const PAGE_LIMIT = 200;

function daysAgoRange(days) {
    if (!days || days <= 0) return null;
    const to = new Date();
    const from = new Date(to.getTime() - days * 86400 * 1000);
    return { from: from.toISOString(), to: to.toISOString() };
}
function stripForEditor(t) {
    if (!t) return {};
    const { id, createdAt, updatedAt, ...rest } = t;
    return rest;
}
function Chip({ children }) {
    return <span className="border border-[color:var(--line-soft)] px-[5px] py-[1px] text-[10px] text-[color:var(--muted-soft)]">{children}</span>;
}

function Fallback({ command }) {
    return (
        <div className="grid h-full place-items-center">
            <div className="grid gap-[10px] text-center max-w-[440px]">
                <span className={`${NOTE_CLASS} text-[10px] uppercase tracking-[0.08em]`}>Signals</span>
                <p className="text-[13px] leading-[1.5] text-[color:var(--muted)]">
                    {command
                        ? `No signal named /${command}. Pick another from the Library sidebar.`
                        : "Pick a signal from the Library sidebar."}
                </p>
            </div>
        </div>
    );
}

export default function SignalsPage({ selectedItem, focusHeader }) {
    // Resolve the selected signal by command, refreshing on store change.
    const [trigger, setTrigger] = useState(() => findTriggerByCommand(null, selectedItem));
    useEffect(() => {
        const reload = () => setTrigger(findTriggerByCommand(null, selectedItem));
        reload();
        return subscribeTriggers(reload);
    }, [selectedItem]);
    // The definition shown is the BACKEND's deployed source (the dashboard keeps
    // no copy of a detector); the built-in entry supplies identity and copy.
    const { ready: catalogReady, meta, source, error: sourceErr } = useSignalSource(trigger?.command);

    // Range over the whole tenant (the Library has no scope picker).
    const [days, setDays] = useState(7);
    const range = useMemo(() => daysAgoRange(Number(days) || 7), [days]);

    // The signal's firing prompts, server-scoped — getTraces INNER-joins
    // SignalTags on the signal scope step, so the list IS the firing prompts.
    const scopePath = useMemo(() => (selectedItem ? [{ dim: "signals", ids: [selectedItem] }] : []), [selectedItem]);
    // Omit the enriched prompt text (ai_sessions sensitive) for a role without
    // that read, or the column policy 403s the whole firing-prompts query.
    // Fail-CLOSED so a non-sensitive role never requests it (no transient 403).
    const caps = useMyPermissions();
    const canReadSensitivePrompts = hasSensitivePermission(caps, PERMISSIONS.AI_SESSIONS_READ_SENSITIVE);
    const { data: traces = [], loading } = useQuery(
        "getTraces",
        { scopePath, range, limit: PAGE_LIMIT, sort: "newest", canReadSensitivePrompts },
        { enabled: !!trigger },
    );

    const ident = trigger || {};
    const draft = useMemo(
        () => signalSourceDefinition(meta, source, stripForEditor(trigger)) || stripForEditor(trigger),
        [meta, source, trigger],
    );
    const stepKinds = useMemo(() => [...new Set((draft.steps || []).map((s) => s.kind).filter(Boolean))], [draft]);
    const definitionLoading = !catalogReady || (Boolean(meta) && source == null);

    if (!trigger) return <Fallback command={selectedItem} />;

    const total = Number(traces?.total) || traces.length || 0;
    const isDefault = isDefaultTrigger(trigger.command);

    const center = (
        <ScrollArea data-content-scroll className="min-h-0 h-full" contentClassName="grid gap-[12px] pb-[18px]">
            <PageHero
                eyebrow="Library · Signal"
                title={(
                    <span className="inline-flex min-w-0 max-w-full items-baseline gap-[10px]">
                        <span className="font-mono">/{ident.command || trigger.command}</span>
                        {ident.title && ident.title !== ident.command ? (
                            <span className="truncate text-[12px] font-normal tracking-normal text-[color:var(--muted-soft)]">{ident.title}</span>
                        ) : null}
                    </span>
                )}
                detail={ident.description || ""}
                metrics={[
                    { label: "Fired", value: String(total), note: "prompts firing this signal" },
                    { label: "Window", value: `${days}d`, note: "lookback for this list" },
                ]}
            />
            <div className="flex flex-wrap items-center gap-[10px]">
                <span className="flex flex-wrap gap-[5px]">
                    <Chip>/{ident.command || trigger.command}</Chip>
                    {stepKinds.map((k) => <Chip key={k}>{k}</Chip>)}
                    {isDefault ? <Chip>built-in default</Chip> : null}
                </span>
                <label className="flex items-center gap-[6px]">
                    <span className="text-[10px] uppercase tracking-[0.04em] text-[color:var(--muted-soft)]">Window · days</span>
                    <input type="number" min={1} value={days} onChange={(e) => setDays(e.target.value)} className={`${INPUT} w-[64px]`} />
                </label>
            </div>
            <span className={NOTE_CLASS}>
                {total} prompt{total === 1 ? "" : "s"} fired this signal in the last {days} days.
            </span>
            <TraceTable
                rows={traces}
                showEndpoint
                loading={loading}
                emptyMessage="No prompts fired this signal in this window."
            />
        </ScrollArea>
    );

    const editor = (
        <ScrollArea className="min-h-0 h-full" contentClassName="grid min-w-0 content-start gap-[10px] py-[10px] pb-[18px] [grid-template-columns:minmax(0,1fr)]" aria-busy={definitionLoading}>
            <div className="flex flex-wrap gap-[5px]">
                <Chip>stream</Chip><Chip>annotate</Chip>
                {stepKinds.map((k) => <Chip key={k}>{k}</Chip>)}
                {isDefault ? <Chip>built-in default</Chip> : null}
            </div>
            {definitionLoading ? (
                <span className={NOTE_CLASS}>Loading definition...</span>
            ) : (
                <SignalSourceEditor key={trigger.id} value={draft} onChange={() => {}} readOnly />
            )}
            <span className="text-[11px] text-[color:var(--muted-soft)]">
                Built-in, read-only signal. Shows prompts that triggered it.
                {sourceErr ? " Source is currently unavailable." : ""}
                {catalogReady && !meta ? " Not in this tenant's signals catalog, so no source to show." : ""}
            </span>
        </ScrollArea>
    );

    return (
        <div className="flex-1 min-h-0 min-w-0 -mx-[var(--main-inset)]">
            <SplitPage
                defaultDetailRatio={0.42}
                resetKey={trigger.id}
                focusHeader={focusHeader}
                rightHeader={<TabStrip tabs={[{ id: "definition", label: "Definition", active: true }]} className="pt-0" />}
                leftContent={() => center}
                rightContent={() => editor}
            />
        </div>
    );
}
